import type { GroupedColumnOrdering } from '@cast/design-system';

/** Columns hidden while reporting monetization limits the console. */
const REPORTING_LIMITED_HIDDEN_COLUMNS = [
  'score',
  'savings',
  'autoscaler',
  'wlAutoscaler',
  'nodeAutoscaler',
  'gpuSharing',
  'liveMigration',
  'rebalanced',
  'hibernation',
] as const;

export type PresetId =
  | 'default'
  | 'reliability'
  | 'compute'
  | 'cost'
  | 'autoscaling'
  | 'metadata';

export const PRESET_LABELS: Record<PresetId, string> = {
  default: 'Default',
  reliability: 'Reliability',
  compute: 'Utilization',
  cost: 'Cost',
  autoscaling: 'Autoscaling',
  metadata: 'Metadata',
};

export const PRESET_ORDER: PresetId[] = [
  'default',
  'reliability',
  'compute',
  'cost',
  'autoscaling',
  'metadata',
];

/** Canonical order for non-pinned columns — mirrors reference LC_MIDDLE */
const MIDDLE_COLUMN_ORDER = [
  'region',
  'nodes',
  'cpuEff',
  'memEff',
  'gpuEff',
  'spot',
  'cost',
  'savings',
  'slo',
  'autoscaler',
  'wlAutoscaler',
  'nodeAutoscaler',
  'gpuSharing',
  'liveMigration',
  'rebalanced',
  'hibernation',
  'latency',
  'errRate',
  'reqRate',
  'oom',
  'restarts',
  'disrupt',
  'nodeAvailability',
  'nodeFailures',
  'cpuSaturation',
] as const;

/** Managed by, score + status keep fixed positions so columns do not jump. */
const ANCHORED_AFTER_NAME = ['managedBy', 'score'] as const;
const ANCHORED_AT_END = ['status'] as const;

const CLUSTERS_TABLE_COLUMN_ORDER: GroupedColumnOrdering[] = [
  { id: 'name' },
  ...ANCHORED_AFTER_NAME.map((id) => ({ id })),
  ...MIDDLE_COLUMN_ORDER.map((id) => ({ id })),
  ...ANCHORED_AT_END.map((id) => ({ id })),
];

/** Visible columns per preset — order here only controls visibility, not layout */
const PRESET_COLUMNS: Record<PresetId, readonly string[]> = {
  default: [
    'name',
    'managedBy',
    'score',
    'region',
    'cpuEff',
    'memEff',
    'gpuEff',
    'cost',
    'slo',
    'status',
  ],
  reliability: [
    'name',
    'managedBy',
    'latency',
    'errRate',
    'reqRate',
    'oom',
    'restarts',
    'disrupt',
    'nodeAvailability',
    'nodeFailures',
    'cpuSaturation',
    'status',
  ],
  compute: [
    'name',
    'managedBy',
    'region',
    'nodes',
    'cpuEff',
    'memEff',
    'gpuEff',
    'spot',
    'status',
  ],
  cost: [
    'name',
    'managedBy',
    'score',
    'region',
    'cost',
    'savings',
    'spot',
    'status',
  ],
  autoscaling: [
    'name',
    'managedBy',
    'region',
    'wlAutoscaler',
    'nodeAutoscaler',
    'gpuSharing',
    'liveMigration',
    'rebalanced',
    'hibernation',
    'status',
  ],
  metadata: ['name', 'managedBy', 'region', 'nodes', 'autoscaler', 'status'],
};

const ALL_TOGGLEABLE = [
  'managedBy',
  'score',
  'region',
  'nodes',
  'cpuEff',
  'memEff',
  'gpuEff',
  'spot',
  'cost',
  'savings',
  'slo',
  'autoscaler',
  'wlAutoscaler',
  'nodeAutoscaler',
  'gpuSharing',
  'liveMigration',
  'rebalanced',
  'hibernation',
  'latency',
  'errRate',
  'reqRate',
  'oom',
  'restarts',
  'disrupt',
  'nodeAvailability',
  'nodeFailures',
  'cpuSaturation',
] as const;

type PresetColumnSettings = {
  hiddenColumns: Record<string, boolean>;
  columnsOrder: GroupedColumnOrdering[];
};

/** Build hiddenColumns + columnsOrder for a preset — mirrors reference lcInitPreset */
export const buildPresetColumnSettings = (
  presetId: PresetId,
  options?: { limitReportingConsole?: boolean }
): PresetColumnSettings => {
  const presetCols = PRESET_COLUMNS[presetId];
  const activeSet = new Set(presetCols);

  const hiddenColumns = ALL_TOGGLEABLE.reduce<Record<string, boolean>>(
    (h, col) => {
      h[col] = !activeSet.has(col);
      return h;
    },
    {}
  );

  if (options?.limitReportingConsole) {
    for (const col of REPORTING_LIMITED_HIDDEN_COLUMNS) {
      hiddenColumns[col] = true;
    }
  }

  return { hiddenColumns, columnsOrder: CLUSTERS_TABLE_COLUMN_ORDER };
};
